import { Button } from "@itk/mui-components";
// eslint-disable-next-line @itk/prefer-itk-imports
import { ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { DekoxIcon } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { addEditLabelActions } from "../add-edit-label/add-edit-label-store";
import { applicationActions } from "src/components/application-store";
import { useParams } from "react-router";

const mapStateToProps = (state: RootState) => ({
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState
});

const ownActions = {
  setSelectedConfigurations: configuratorActions.setSelectedConfigurations,
  deleteConfigurationsRequest: configuratorActions.deleteConfigurationsRequest,
  copyProjectLineItemsRequest: configuratorActions.copyProjectLineItemsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  setIsShowingAddEditLabel: addEditLabelActions.setIsShowingAddEditLabel,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const SelectedConfigurationsBase = (props: Props) => {
  const {
    selectedConfigurations,
    setSelectedConfigurations,
    setIsShowingAddEditLabel,
    setWarningAction,
    deleteConfigurationsRequest,
    getConfigurationsRequest,
    copyProjectLineItemsRequest,
    getLabelsRequest,
    configurationsState
  } = props;

  const theme = useTheme();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCopyOptions, setShowCopyOptions] = useState(false);

  const handleMenuClose = () => {
    setAnchorEl(null);
    setShowCopyOptions(false);
  };

  const handleAddLabel = () => {
    handleMenuClose();
    setIsShowingAddEditLabel(true);
  };

  const firstProjectLineItemsOfConfigurations = useMemo(() => {
    return selectedConfigurations.reduce((acc, selectedConfiguration) => {
      const singleConfiguration = configurationsState?.configurations.find(configuration => configuration.configurationId === selectedConfiguration);
      if (singleConfiguration) {
        const firstProjectLineItem = singleConfiguration.projectLineItems?.find(projectLineItem => projectLineItem.parentProjectLineItemId === null);
        if (firstProjectLineItem?.projectLineItemId) {
          acc.push(firstProjectLineItem.projectLineItemId);
        }
      }
      return acc;
    }, [] as string[]);
  }, [selectedConfigurations, configurationsState]);

  const handleCopy = (blankValues: boolean) => {
    handleMenuClose();
    copyProjectLineItemsRequest(blankValues, { projectLineItemIds: firstProjectLineItemsOfConfigurations }, isSuccess => {
      if (isSuccess) {
        getConfigurationsRequest(projectId);
        getLabelsRequest(projectId);
        setSelectedConfigurations([]);
      }
    });
  };

  const handleDelete = () => {
    handleMenuClose();
    setWarningAction({
      confirm: () => {
        deleteConfigurationsRequest({ configurationIds: selectedConfigurations }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
            setSelectedConfigurations([]);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteSelectedConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        },
        messageIcon: <DekoxIcon icon="delete" />
      },
      variant: "confirmCancel"
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" px={3} py={1.5} bgcolor={theme.palette.customColors.white} borderTop={`1px solid ${theme.palette.customColors.grey}`}>
      <Typography>{genericTranslationsT.selected(selectedConfigurations.length.toString())}</Typography>
      <Stack direction="row" alignContent="center" gap={2}>
        <Button
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="cancel"
          variant="outlined"
          onClick={() => {
            setSelectedConfigurations([]);
          }}
        />
        <Button
          t={genericTranslationsT}
          k={genericTranslationsK}
          field="actions"
          variant="contained"
          onClick={e => {
            setAnchorEl(e.currentTarget);
          }}
        />
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
        elevation={2}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              minWidth: 180,
              boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
              transform: "translateY(-10px) !important"
            }
          }
        }}
      >
        {!showCopyOptions ? (
          <>
            <MenuItem onClick={handleAddLabel} sx={{ gap: 0.4, py: 1.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
              <ListItemIcon>
                <DekoxIcon icon="add" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography>{configuratorPageT.addLabel}</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => setShowCopyOptions(true)} sx={{ gap: 0.4, py: 1.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
              <ListItemIcon>
                <DekoxIcon icon="copy" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography>{genericTranslationsT.copy}</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ gap: 0.4, py: 1.4 }}>
              <ListItemIcon>
                <DekoxIcon icon="delete" color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography color="error">{genericTranslationsT.delete}</Typography>
              </ListItemText>
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => setShowCopyOptions(false)} sx={{ gap: 0.4, py: 0.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
              <ListItemIcon>
                <DekoxIcon icon="arrow-back" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography>{genericTranslationsT.back}</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleCopy(true)} sx={{ gap: 0.4, py: 1.4, borderBottom: `1px solid ${theme.palette.customColors.grey}` }}>
              <ListItemIcon>
                <DekoxIcon icon="copy" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography>{genericTranslationsT.copyBlank}</Typography>
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleCopy(false)} sx={{ gap: 0.4, py: 1.4 }}>
              <ListItemIcon>
                <DekoxIcon icon="copy" fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography>{genericTranslationsT.copyWithValues}</Typography>
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Stack>
  );
};

export const SelectedConfigurations = connect(mapStateToProps, ownActions)(SelectedConfigurationsBase);
